import { useCallback, useRef } from "react";
import type { Component, Viewport } from "@/types";
import type { Bounds } from "./InteractionContext";

// Performance configuration (simplified)
export const CANVAS_PERFORMANCE_CONFIG = {
  VIRTUALIZATION_THRESHOLD: 50, // Number of components before enabling virtualization
  FRAME_BUDGET_MS: 16, // Target 60fps
  DEBOUNCE_DELAY_MS: 100, // Debounce for expensive operations
};

/**
 * Hook for spatial indexing (STUB)
 * @deprecated Spatial indexing has been removed.
 */
export const useSpatialIndexing = (
  _components: Component[],
  _viewport: Viewport,
  _enabled: boolean = true
): null => {
  return null;
};

/**
 * Hook for viewport culling (simplified)
 * In flex layout, all components are visible within their containers.
 */
export const useViewportCulling = (
  components: Component[],
  _viewportBounds: Bounds | null,
  _spatialIndex: null,
  _enabled: boolean = true
): Component[] => {
  return components;
};

// Performance monitoring hook
export const usePerformanceMonitor = (enabled: boolean = false) => {
  const metricsRef = useRef({
    renderCount: 0,
    lastRenderTime: 0,
    frameTime: 0,
    componentCount: 0,
  });

  const startMeasure = useCallback(() => {
    if (!enabled) return;
    metricsRef.current.lastRenderTime = performance.now();
  }, [enabled]);

  const endMeasure = useCallback((componentCount: number) => {
    if (!enabled) return;

    const endTime = performance.now();
    const frameTime = endTime - metricsRef.current.lastRenderTime;

    metricsRef.current.renderCount++;
    metricsRef.current.frameTime = frameTime;
    metricsRef.current.componentCount = componentCount;

    if (frameTime > CANVAS_PERFORMANCE_CONFIG.FRAME_BUDGET_MS) {
      console.warn(
        `[CanvasCore] Frame budget exceeded: ${frameTime.toFixed(2)}ms for ${componentCount} components`
      );
    }
  }, [enabled]);

  return { startMeasure, endMeasure, metrics: metricsRef.current };
};
